import { Email as EmailIcon, VideoLibrary as VideoLibraryIcon, VpnKey as VpnKeyIcon } from '@mui/icons-material'
import {
  Button,
  CircularProgress,
  Container,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Theme,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@mui/material'
import React from 'react'
import { useNavigate } from 'react-router'
import Movie from 'redux/models/movie'
import { InfoActions, InfoStoreStates } from '.'
import Modal from './Modal'
import { IdText, VerificationCell } from './styles'

type OwnProps = {}
type InfoProps = OwnProps & InfoStoreStates & InfoActions

const Info = (props: InfoProps) => {
  const { auth, movies } = props
  const { user } = auth
  const navigate = useNavigate()

  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'))

  const [showDeleteAccountModal, setShowDeleteAccountModal] = React.useState(false)
  const [password, setPassword] = React.useState('')
  const [activeMovie, setActiveMovie] = React.useState<Movie | null>(null)
  const [preAccountDeleting, setPreAccountDeleting] = React.useState(false)

  const onDeleteMovie = (movie: Movie) => {
    if (!window.confirm('この動画を削除してもよろしいですか?')) return
    props.cancelMovie(movie)
  }

  const onModalClose = () => {
    setShowDeleteAccountModal(false)
    setPassword('')
  }

  const onDeleteAccount = () => {
    const { deleteAccount } = props
    if (!window.confirm('アカウントを削除してもよろしいですか?')) return

    onModalClose()
    deleteAccount(password)
  }

  React.useEffect(() => {
    props.load()
  }, [])

  React.useEffect(() => {
    if (!preAccountDeleting || auth.accountDeleting) {
      setPreAccountDeleting(auth.accountDeleting)
      return
    }

    if (auth.deleteAccountError) window.alert(auth.deleteAccountError)

    setPreAccountDeleting(auth.accountDeleting)
  }, [preAccountDeleting, auth.accountDeleting, auth.deleteAccountError])

  if (!user) return <></>

  const items = movies.items.filter((item) => item.userId === user?.id && !item.isVerified && !item.isCancelled)

  return (
    <Container>
      <CssBaseline />
      <Paper sx={{ p: 1, mb: 2 }}>
        <Grid
          container
          direction="column"
          spacing={2}
          sx={{
            alignItems: 'center',
          }}>
          <Grid
            container
            spacing={2}
            sx={{
              alignItems: 'baseline',
            }}>
            <Grid>
              <Typography variant="h3" fontWeight="bold">
                {user.name}
              </Typography>
            </Grid>
          </Grid>
          <Grid container>
            <List>
              <ListItem>
                <ListItemIcon>
                  <EmailIcon />
                </ListItemIcon>
                <ListItemText primary={user.email} />
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{ ml: 2 }}
                  onClick={() => navigate('/mypage/changeEmail')}>
                  変更
                </Button>
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <VpnKeyIcon />
                </ListItemIcon>
                <ListItemText primary="＊＊＊＊＊＊＊＊＊＊＊＊＊" />
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{ ml: 2 }}
                  onClick={() => navigate('/mypage/changePassword')}>
                  変更
                </Button>
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <VideoLibraryIcon />
                </ListItemIcon>
                <ListItemText
                  primary={
                    user.numberOfMovies > 0
                      ? `${user.numberOfMovies}本（検証済：${user.numberOfVerifiedMovies}本）`
                      : `${user.numberOfMovies}本`
                  }
                />
              </ListItem>
            </List>
          </Grid>
        </Grid>
      </Paper>
      <Paper sx={{ p: 1, mb: 2 }}>
        <Toolbar>
          <Grid size="grow">
            <Typography variant="h6" fontWeight="bold">
              発話シーン投稿履歴
            </Typography>
          </Grid>
        </Toolbar>
        <TableContainer>
          <Table size="small" aria-label="word list">
            <TableHead>
              <TableRow>
                <TableCell>発話ワード</TableCell>
                {!mdDown && (
                  <>
                    <TableCell>投稿日時</TableCell>
                    <TableCell>状態</TableCell>
                    <TableCell>視聴</TableCell>
                    <TableCell>削除</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((row) => (
                <TableRow key={row.id}>
                  {!mdUp && (
                    <TableCell component="td" scope="row">
                      <IdText component="p">{row.createdDate}</IdText>
                      {row.word}
                      <IdText component="p">
                        <Button
                          variant="contained"
                          component="label"
                          color="primary"
                          onClick={() => setActiveMovie(row)}>
                          動画を見る
                        </Button>
                      </IdText>
                    </TableCell>
                  )}

                  {!mdDown && (
                    <>
                      <TableCell component="td" scope="row">
                        {row.word}
                      </TableCell>
                      <TableCell component="td" scope="row">
                        {row.createdDate}
                      </TableCell>
                      <TableCell component="td" scope="row">
                        {row.status}
                      </TableCell>
                      <VerificationCell component="td" scope="row">
                        <Button
                          variant="contained"
                          component="label"
                          color="primary"
                          onClick={() => setActiveMovie(row)}>
                          動画を見る
                        </Button>
                      </VerificationCell>
                      <VerificationCell component="td" scope="row">
                        <Button
                          variant="contained"
                          component="label"
                          color="secondary"
                          onClick={() => onDeleteMovie(row)}>
                          削除
                        </Button>
                      </VerificationCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <Paper sx={{ p: 1, mb: 2 }}>
        <Grid
          container
          spacing={2}
          sx={{
            alignItems: 'center',
          }}>
          <Grid
            size={{
              md: 'grow',
              sm: 12,
              xs: 12,
            }}>
            <Typography variant="h6">アカウントの削除</Typography>
          </Grid>
          {auth.accountDeleting && (
            <Grid>
              <CircularProgress size={20} />
            </Grid>
          )}
          <Grid
            container
            sx={{
              justifyContent: 'flex-end',
            }}
            size={{
              md: 2,
              sm: 12,
              xs: 12,
            }}>
            <Button
              size="large"
              variant="contained"
              color="secondary"
              sx={{ ml: 2 }}
              onClick={() => setShowDeleteAccountModal(true)}>
              削除
            </Button>
          </Grid>
        </Grid>
      </Paper>
      <Dialog open={showDeleteAccountModal} onClose={() => onModalClose()} aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">アカウントの削除</DialogTitle>
        <DialogContent>
          <DialogContentText>アカウントを削除するには現在のパスワードを入力してください</DialogContentText>
          <TextField
            autoFocus
            variant="standard"
            size="small"
            type="password"
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onModalClose()} color="primary">
            キャンセル
          </Button>
          <Button onClick={() => onDeleteAccount()} color="secondary">
            削除
          </Button>
        </DialogActions>
      </Dialog>
      {activeMovie && <Modal item={activeMovie} onClose={() => setActiveMovie(null)} />}
    </Container>
  )
}

export default Info
